import isObject from 'lodash/isObject'
import * as Sqrl from 'squirrelly/dist/browser/squirrelly.min.js'
import config from 'src/config.js'
import APPS from '@constants/apps'
import { useSpace } from '@/store/modules/App/space'
import { useComponents } from 'src/store/modules/App/component'
import { useUser } from 'src/store/modules/App/user'
import type { BlockContent } from '@/types/content'
import { stripHtml, truncate } from '@utils/formats/index'
import last from '@utils/last'
import dayjs from '@utils/libraries/dayjs'
import {
  isString,
  isArray,
  hasObjectConstructor,
  isUndefined,
  isRichtext,
} from '@/utils/checkers/validation'
import forEach from '@/utils/forEach'
// we need to import the browser package because the other includes node.js code
// see https://github.com/squirrellyjs/squirrelly/issues/240
import { useApps } from 'src/utils/helpers/isAppInstalled'
import { parseLegacyTemplate } from './squirrelyHelpers'

export default function isBlokArray(
  blokArray: BlockContent[] | BlockContent,
): boolean {
  if (isArray(blokArray)) {
    // check for all positions in the array
    const hasBlokElement = blokArray.find((el: BlockContent): boolean => {
      return (
        el &&
        typeof el._uid !== 'undefined' &&
        typeof el.component !== 'undefined'
      )
    })
    return Boolean(blokArray.length) && Boolean(hasBlokElement)
  }
  return false
}

/**
 * @function getBlocksInStory
 * @param {Object} contentTree
 * @returns {Array<String>} array of blocks present in the story
 */
export const getBlocksInStory = (contentTree: BlockContent): BlockContent[] => {
  const enrich = function (
    block: BlockContent | BlockContent[],
    blocks = [] as BlockContent[],
    parentName: string = '',
  ): void {
    if (Array.isArray(block) && isBlokArray(block)) {
      forEach(block, function (val: BlockContent, _key) {
        if (val && isObject(val)) {
          enrich(val, blocks, parentName)
        }
      })
    } else if (isObject(block)) {
      if ((block as BlockContent).component) {
        blocks.push({ ...block, parent_name: parentName } as BlockContent)
      }

      forEach(
        block as { [key: string]: BlockContent },
        function (val: BlockContent, _key) {
          if (isBlokArray(val)) {
            enrich(val, blocks, (block as BlockContent).component)
          }
        },
      )
    }
  }

  const blocks: BlockContent[] = []
  enrich(contentTree, blocks)

  return blocks
}

Sqrl.helpers.define(
  'image',
  ({ params }: { params: Array<string> }): string => {
    const source = params[0]
    if (!source) {
      return ''
    }

    const hasWhitespaceEncoded = (url: string): boolean => {
      const regex = /%20|\+/
      return regex.test(url)
    }

    if (
      (hasCloudinaryAppInstalled() && hasWhitespaceEncoded(source)) ||
      hasWhitespaceEncoded(source)
    ) {
      return `<img src="${source}" />`
    }

    return `<img src="${encodeURI(source)}" />`
  },
)

Sqrl.helpers.define('greaterThan', {
  // @ts-expect-error - keep working as is but this looks probably unused
  helperStart(param: string) {
    return 'if(' + param.replace(',', '>') + '){'
  },
  helperEnd() {
    return '}'
  },
  blocks: {
    else() {
      return '}else{'
    },
  },
})

const hasCloudinaryAppInstalled = (): boolean => {
  const { isInstalled } = useApps()

  return isInstalled(APPS.cloudinaryAssetManager)
}

/**
 * return a language with transformed characters if they exist
 * @function getSanitizedLang
 * @param   {String} lang
 * @return  {String}
 */
const getSanitizedLang = (lang: string): string => {
  if (isString(lang)) {
    return lang.replace('-', '_')
  }

  return ''
}

/**
 * @function getFirstTextItemInRichtext
 * @param {Object}} richtextContent
 * @returns {String}
 */
const getFirstTextItemInRichtext = (richtextContent: unknown): string => {
  // @ts-expect-error - richtextContent is unknown until we refactor this
  const firstContent = richtextContent.content[0] || {}

  if (
    isArray(firstContent.content) &&
    hasObjectConstructor(firstContent.content[0])
  ) {
    return firstContent.content[0].text || ''
  }

  return ''
}

/**
 * receives a block and return a preview template for this block
 * based on component configuration
 *
 * @function getBlockTemplate
 * @param    {Object} block
 * @returns  {String}
 */
export const getBlockTemplate = (block: BlockContent): string => {
  const componentStore = useComponents()
  const spaceStore = useSpace()
  if (!block || !componentStore) {
    return ''
  }

  const blok = { ...block }
  const currentComponent = componentStore.components.find((component) => {
    return component.name === blok.component
  })

  const getBlokDefinition = (
    blokKey: string | number,
  ): { type?: string; disable_time?: boolean } => {
    const blok =
      currentComponent?.schema &&
      Object.entries(currentComponent?.schema).find((fieldEntry) => {
        return fieldEntry[0] === blokKey
      })

    const value = blok && blok[1]

    return isUndefined(value) ? {} : value
  }

  forEach(block, (_, key) => {
    const locale = `__i18n__${getSanitizedLang(spaceStore.lang)}`
    const userStore = useUser()
    const timezone = userStore.getUserInfo.timezone

    if (isString(key) && key.indexOf(locale) > -1) {
      blok[key.replace(locale, '')] = blok[key]
    }
    const blockDefinition = getBlokDefinition(key)

    if (blockDefinition.type === 'datetime') {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- this is an ugly cast but we check for validity below
      const datetime = blok[key] as string
      const disableTimeSelection = !!blockDefinition.disable_time

      blok[key] = dayjs(datetime).isValid()
        ? disableTimeSelection
          ? dayjs.utc(datetime).format('YYYY-MM-DD')
          : dayjs.utc(datetime).tz(timezone).format('YYYY-MM-DD HH:mm')
        : ''
    }
  })

  if (currentComponent?.preview_tmpl) {
    let tmpl = ''
    let currentTemplate = currentComponent?.preview_tmpl || ''

    currentTemplate = parseLegacyTemplate(currentTemplate, blok)

    // we need to always replace this secure replacement made by the backend
    currentTemplate = currentTemplate
      .replaceAll('&gt;', '>')
      .replaceAll('&lt;', '<')

    try {
      tmpl = Sqrl.render(currentTemplate, blok)
    } catch (e) {
      // @ts-expect-error - e is unknown until we refactor this
      console.error(`Error rendering the preview template: ${e.message}`)
    }
    return tmpl.length
      ? `<div class="blok-template__tcaption">${tmpl}</div>`
      : ''
  }

  const orderedBloks = Object.fromEntries(
    Object.keys(currentComponent?.schema || {}).map((key) => [key, blok[key]]),
  )

  const keysFiltered = Object.keys(orderedBloks).filter((key) => {
    const value = blok[key]

    if (isString(key) && key.indexOf('__i18n__') > -1) {
      return false
    }

    if (currentComponent && currentComponent?.preview_field) {
      return (
        (isRichtext(value) || (isString(value) && value.length)) &&
        key === currentComponent.preview_field
      )
    }

    return (
      key !== '_uid' &&
      key !== 'component' &&
      (isRichtext(value) || (isString(value) && value.length))
    )
  })

  if (keysFiltered[0]) {
    const blokPreview = blok[keysFiltered[0]]

    const preview = isRichtext(blokPreview)
      ? getFirstTextItemInRichtext(blokPreview)
      : (blokPreview as string)

    let stripped = stripHtml(preview)

    if (stripped.indexOf(`//${config.get('bucket')}`) === 0) {
      stripped = last(stripped, '/') ?? ''
    }

    if (stripped === '') {
      return ''
    }

    return (
      '<span class="blok-template__caption">' +
      truncate(250, stripped) +
      '</span>'
    )
  }

  return ''
}
